import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useProspects } from '../hooks/useProspects';
import { StateBadge } from '../components/StateBadge';
import { ScoreBadge } from '../components/ScoreBadge';
import type { ProspectState } from '../types';

const STATES: ProspectState[] = [
  'DISCOVERED',
  'FILTERED_OUT',
  'QUALIFIED',
  'CONTACT_FOUND',
  'CONTACT_VERIFIED',
  'DRAFT_READY',
  'AWAITING_APPROVAL',
  'APPROVED',
  'SENT',
  'REPLIED',
  'BOUNCED',
  'SUPPRESSED',
  'HIGH_INTENT',
];

type SortBy = 'score' | 'date' | 'intent';

export function Prospects() {
  const navigate = useNavigate();
  const [state, setState] = useState<ProspectState | ''>('');
  const [sortBy, setSortBy] = useState<SortBy>('score');
  const [page, setPage] = useState(1);

  const { data, isLoading, isError } = useProspects({
    state: state || undefined,
    sortBy,
    page,
    pageSize: 25,
  });

  const prospects = data?.items ?? [];
  const total = data?.total ?? 0;
  const hasMore = data?.hasMore ?? false;

  return (
    <div style={{ padding: '32px 36px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <div>
          <h1 style={{ margin: '0 0 4px', fontSize: '24px', fontWeight: 800, color: '#111827' }}>
            Prospects
          </h1>
          <p style={{ margin: 0, fontSize: '14px', color: '#9ca3af' }}>
            {total.toLocaleString()} total prospects
          </p>
        </div>
      </div>

      {/* Filter Bar */}
      <div
        style={{
          display: 'flex',
          gap: '12px',
          alignItems: 'center',
          padding: '14px 18px',
          backgroundColor: '#fff',
          border: '1px solid #e5e7eb',
          borderRadius: '10px',
          marginBottom: '20px',
        }}
      >
        <label style={{ fontSize: '13px', fontWeight: 600, color: '#374151' }}>
          State
          <select
            value={state}
            onChange={(e) => { setState(e.target.value as ProspectState | ''); setPage(1); }}
            style={{
              marginLeft: '8px',
              padding: '5px 10px',
              borderRadius: '6px',
              border: '1px solid #d1d5db',
              fontSize: '13px',
              color: '#111827',
            }}
          >
            <option value="">All States</option>
            {STATES.map((s) => (
              <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>
            ))}
          </select>
        </label>

        <label style={{ fontSize: '13px', fontWeight: 600, color: '#374151' }}>
          Sort by
          <select
            value={sortBy}
            onChange={(e) => { setSortBy(e.target.value as SortBy); setPage(1); }}
            style={{
              marginLeft: '8px',
              padding: '5px 10px',
              borderRadius: '6px',
              border: '1px solid #d1d5db',
              fontSize: '13px',
              color: '#111827',
            }}
          >
            <option value="score">Overall Score</option>
            <option value="date">Date Created</option>
            <option value="intent">Intent Score</option>
          </select>
        </label>

        {isLoading && <span style={{ fontSize: '12px', color: '#9ca3af', marginLeft: 'auto' }}>Loading…</span>}
      </div>

      {isError && (
        <div style={{ padding: '14px', backgroundColor: '#fee2e2', borderRadius: '8px', color: '#991b1b', marginBottom: '16px', fontSize: '14px' }}>
          Failed to load prospects.
        </div>
      )}

      {/* Table */}
      <div
        style={{
          backgroundColor: '#fff',
          border: '1px solid #e5e7eb',
          borderRadius: '10px',
          overflow: 'hidden',
        }}
      >
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ backgroundColor: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
              {['Company', 'State', 'ICP', 'Pain', 'Intent', 'Contact', 'Created'].map((h) => (
                <th
                  key={h}
                  style={{
                    padding: '12px 16px',
                    textAlign: 'left',
                    fontSize: '12px',
                    fontWeight: 700,
                    color: '#6b7280',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                  }}
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {prospects.map((p, i) => (
              <tr
                key={p.id}
                onClick={() => navigate(`/prospects/${p.id}`)}
                style={{
                  borderBottom: i < prospects.length - 1 ? '1px solid #f3f4f6' : 'none',
                  cursor: 'pointer',
                  transition: 'background 0.1s',
                }}
                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f8fafc')}
                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
              >
                <td style={{ padding: '13px 16px' }}>
                  <div style={{ fontWeight: 600, fontSize: '14px', color: '#111827' }}>{p.companyName}</div>
                  <div style={{ fontSize: '12px', color: '#9ca3af' }}>{p.domain}</div>
                </td>
                <td style={{ padding: '13px 16px' }}>
                  <StateBadge state={p.state} />
                </td>
                <td style={{ padding: '13px 16px' }}>
                  <ScoreBadge score={p.scores.icp} size="sm" />
                </td>
                <td style={{ padding: '13px 16px' }}>
                  <ScoreBadge score={p.scores.pain} size="sm" />
                </td>
                <td style={{ padding: '13px 16px' }}>
                  <ScoreBadge score={p.scores.intent} size="sm" />
                </td>
                <td style={{ padding: '13px 16px' }}>
                  {p.contact ? (
                    <span
                      style={{
                        fontSize: '12px',
                        fontWeight: 600,
                        padding: '2px 8px',
                        borderRadius: '4px',
                        backgroundColor: p.contact.verified ? '#d1fae5' : '#fef3c7',
                        color: p.contact.verified ? '#065f46' : '#92400e',
                      }}
                    >
                      {p.contact.verified ? '✓ Verified' : '~ Unverified'}
                    </span>
                  ) : (
                    <span style={{ fontSize: '12px', color: '#d1d5db' }}>—</span>
                  )}
                </td>
                <td style={{ padding: '13px 16px', fontSize: '12px', color: '#6b7280', whiteSpace: 'nowrap' }}>
                  {new Date(p.createdAt).toLocaleDateString()}
                </td>
              </tr>
            ))}
            {prospects.length === 0 && !isLoading && (
              <tr>
                <td colSpan={7} style={{ padding: '40px', textAlign: 'center', color: '#9ca3af', fontSize: '14px' }}>
                  No prospects found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {(page > 1 || hasMore) && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: '10px', marginTop: '20px' }}>
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            style={{
              padding: '7px 16px',
              borderRadius: '6px',
              border: '1px solid #d1d5db',
              backgroundColor: '#fff',
              cursor: page === 1 ? 'not-allowed' : 'pointer',
              fontSize: '13px',
              color: '#374151',
            }}
          >
            ← Previous
          </button>
          <span style={{ fontSize: '13px', color: '#6b7280', alignSelf: 'center' }}>
            Page {page}
          </span>
          <button
            onClick={() => setPage((p) => p + 1)}
            disabled={!hasMore}
            style={{
              padding: '7px 16px',
              borderRadius: '6px',
              border: '1px solid #d1d5db',
              backgroundColor: '#fff',
              cursor: !hasMore ? 'not-allowed' : 'pointer',
              fontSize: '13px',
              color: '#374151',
            }}
          >
            Next →
          </button>
        </div>
      )}
    </div>
  );
}
